import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, Crown, RefreshCw, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { refreshRevenueCatSubscription } from '../api/dataService';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useNativeCaps } from '../context/NativeCapsContext';
import {
  ULTRA_MONTHLY_PRICE_LABEL,
  getSubscriptionPageState,
  getSubscriptionResultStatus,
} from '../utils/subscriptionPageState';

const SubscriptionPage = () => {
  const { user, updateUser } = useAuth();
  const { locale, t } = useLocale();
  const { isNativeApp, caps, postToNative } = useNativeCaps();
  const [status, setStatus] = useState({ action: null, state: 'idle', message: null });
  const { isUltra, canUseNativePurchases, planLabel, renewalLabel } = getSubscriptionPageState({
    user,
    isNativeApp,
    caps,
    locale,
    t,
  });
  const actionLabels = {
    purchase: t('subscription.actions.purchase', 'Start Ultra in iOS app'),
    restore: t('subscription.actions.restore', 'Already subscribed? Restore purchase'),
    manage: t('subscription.actions.manage', 'Manage subscription'),
  };
  const features = [
    {
      title: t('subscription.features.handsFreeTitle', 'Hands-free AI Coach during workouts'),
      description: t(
        'subscription.features.handsFreeBody',
        'Start voice-guided coaching without watching ads first, so you can train without staring at the phone.'
      ),
    },
    {
      title: t('subscription.features.formCheckTitle', 'Form Check inside the workout timer'),
      description: t(
        'subscription.features.formCheckBody',
        'Launch Form Check directly from timed workout sessions when you want posture feedback mid-workout.'
      ),
    },
    {
      title: t(
        'subscription.features.higherLimitsTitle',
        'Higher verification limits across voice, meal, and form check'
      ),
      description: t(
        'subscription.features.higherLimitsBody',
        'Get a larger included quota for regular feedback across voice notes, meal verification, and form checks.'
      ),
    },
    {
      title: t('subscription.features.rescheduleTitle', 'More room to reschedule your plan'),
      description: t(
        'subscription.features.rescheduleBody',
        'Adjust goals and regenerate weekly workouts more often when your schedule changes.'
      ),
    },
  ];

  useEffect(() => {
    const handleNativeSubscription = (event) => {
      const payload = event?.detail;
      if (!payload || payload.type !== 'FORZA_SUBSCRIPTION_RESULT') {
        return;
      }

      if (payload.user) {
        updateUser(payload.user);
      }

      setStatus(getSubscriptionResultStatus(payload, t));
    };

    window.addEventListener('forza:native', handleNativeSubscription);
    return () => window.removeEventListener('forza:native', handleNativeSubscription);
  }, [t, updateUser]);

  const sendNativeAction = (type, action) => {
    if (!canUseNativePurchases) {
      setStatus({
        action,
        state: 'error',
        message: t(
          'subscription.nativeUnavailable',
          'Open Forza on iPhone to purchase, restore, or manage Ultra with Apple IAP.'
        ),
      });
      return;
    }

    setStatus({
      action,
      state: 'pending',
      message: t('subscription.status.pending', '{action} started...', {
        action: actionLabels[action] || t('subscription.status.genericAction', 'Subscription action'),
      }),
    });
    postToNative({ type });
  };

  const refreshStatus = async () => {
    setStatus({
      action: 'refresh',
      state: 'pending',
      message: t('subscription.status.refreshing', 'Refreshing subscription status...'),
    });
    try {
      const response = await refreshRevenueCatSubscription();
      if (response?.data?.user) {
        updateUser(response.data.user);
      }
      setStatus({
        action: 'refresh',
        state: 'success',
        message: response?.data?.subscription?.status === 'ultra'
          ? t('subscription.status.successActive', 'Ultra access is active on this account.')
          : t('subscription.status.successInactive', 'Purchase state refreshed. Ultra is not active yet.'),
      });
    } catch (err) {
      setStatus({
        action: 'refresh',
        state: 'error',
        message: err?.response?.data?.message || t('subscription.status.refreshFailed', 'Unable to refresh subscription status.'),
      });
    }
  };

  const pending = status.state === 'pending';

  return (
    <div className="min-h-screen overflow-hidden bg-[#071013] text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.2),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(20,184,166,0.16),_transparent_30%)]" />
      <main className="relative mx-auto flex max-w-6xl flex-col gap-10 px-5 py-10 sm:px-8 lg:flex-row lg:items-center lg:py-16">
        <section className="flex-1">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-semibold text-cyan-100">
            <Crown className="h-4 w-4" />
            {t('subscription.badge', 'Forza Ultra')}
          </div>
          <h1 className="mt-6 text-5xl font-black tracking-[-0.05em] text-white sm:text-6xl">
            {t('subscription.heroTitle', 'Get guided workouts, faster checks, and more control.')}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
            {t(
              'subscription.heroBody',
              'Ultra is for members who want hands-free AI coaching, in-workout form checks, and more flexibility when plans change.'
            )}
          </p>

          <div className="mt-8 grid gap-3">
            {features.map((feature) => (
              <div key={feature.title} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-cyan-300" />
                <div className="min-w-0">
                  <p className="text-base font-bold text-white">{feature.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-300">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full rounded-[2rem] border border-white/10 bg-slate-950/80 p-6 shadow-2xl shadow-cyan-950/40 backdrop-blur lg:max-w-md">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-cyan-300">
                {t('subscription.currentPlan', 'Current plan')}
              </p>
              <h2 className="mt-2 text-3xl font-black">{planLabel}</h2>
            </div>
            <div className="rounded-2xl bg-cyan-300 px-4 py-3 text-sm font-black text-slate-950">
              {isUltra ? t('subscription.active', 'Active') : t('subscription.monthly', 'Monthly')}
            </div>
          </div>

          <div className="mt-6 border-y border-white/10 py-5">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
              {t('subscription.badge', 'Forza Ultra')}
            </p>
            <p className="mt-2 text-4xl font-black text-white">{ULTRA_MONTHLY_PRICE_LABEL}</p>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              {t(
                'subscription.billingNote',
                'Renews monthly until cancelled. You can cancel anytime in your Apple account subscription settings.'
              )}
            </p>
            {renewalLabel ? (
              <p className="mt-2 text-sm font-semibold text-cyan-200">{renewalLabel}</p>
            ) : null}
          </div>

          {status.message ? (
            <div
              className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                status.state === 'error'
                  ? 'border-red-400/30 bg-red-400/10 text-red-100'
                  : 'border-cyan-300/30 bg-cyan-300/10 text-cyan-100'
              }`}
            >
              {status.message}
            </div>
          ) : null}

          <div className="mt-6 grid gap-3">
            {!isUltra ? (
              <button
                type="button"
                onClick={() => sendNativeAction('FORZA_SUBSCRIPTION_PURCHASE', 'purchase')}
                disabled={pending || !canUseNativePurchases || !user}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-300 px-5 py-4 font-black text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('subscription.actions.purchase', 'Start Ultra in iOS app')}
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => sendNativeAction('FORZA_SUBSCRIPTION_RESTORE', 'restore')}
              disabled={pending || !canUseNativePurchases || !user}
              className="text-xs inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300/30 px-5 py-4 font-bold text-cyan-100 transition hover:border-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-5 w-5" />
              {t('subscription.actions.restore', 'Already subscribed? Restore purchase')}
            </button>

            <button
              type="button"
              onClick={() => sendNativeAction('FORZA_SUBSCRIPTION_MANAGE', 'manage')}
              disabled={pending || !canUseNativePurchases || !user}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 px-5 py-4 font-bold text-slate-200 transition hover:border-white/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShieldCheck className="h-5 w-5" />
              {t('subscription.actions.manage', 'Manage subscription')}
            </button>

            {user ? (
              <button
                type="button"
                onClick={refreshStatus}
                disabled={pending}
                className="text-sm font-semibold text-slate-300 underline underline-offset-4 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('subscription.refreshEntitlement', 'Refresh server entitlement')}
              </button>
            ) : (
              <Link to="/login" className="text-center text-sm font-semibold text-cyan-200 underline underline-offset-4">
                {t('subscription.signInBeforePurchasing', 'Sign in before purchasing Ultra')}
              </Link>
            )}
          </div>

          <p className="mt-5 text-center text-xs leading-5 text-slate-400">
            {t('subscription.paymentNoticeLead', 'Payment is processed by Apple. By purchasing, you agree to the')}{' '}
            <Link to="/terms-of-service" className="font-semibold text-cyan-200 underline underline-offset-4">
              {t('auth.termsOfService', 'Terms of Service')}
            </Link>{' '}
            {t('subscription.and', 'and')}{' '}
            <Link to="/privacy-policy" className="font-semibold text-cyan-200 underline underline-offset-4">
              {t('auth.privacyPolicy', 'Privacy Policy')}
            </Link>.
          </p>
        </section>
      </main>
    </div>
  );
};

export default SubscriptionPage;
